// SQLite-based Offline Storage for ACCTOO
// Replaces IndexedDB with SQLite for better persistence and multi-device support

import databaseManager from '../database/index.js';

class SQLiteOfflineStorage {
  constructor() {
    this.isInitialized = false;
    this.userId = null;
    this.companyId = null;
    this.deviceId = null;
  }

  // Initialize SQLite storage
  async init(deviceId, companyId, userId = 'default_user') {
    try {
      this.deviceId = deviceId;
      this.companyId = companyId;
      this.userId = userId;

      console.log('🔄 Initializing SQLite offline storage...', {
        deviceId,
        companyId,
        userId
      });

      // Initialize database manager
      await databaseManager.init(userId, companyId, deviceId);

      this.isInitialized = true;
      console.log('✅ SQLite offline storage initialized successfully');
      
      return { success: true };
    } catch (error) {
      console.error('❌ SQLite offline storage initialization failed:', error);
      throw error;
    }
  }

  // =============================================
  // DATA OPERATIONS
  // =============================================

  // Store data in SQLite
  async storeData(dataType, data) {
    try {
      if (!this.isInitialized) {
        throw new Error('SQLite storage not initialized');
      }

      console.log(`💾 Storing ${data.length} ${dataType} records in SQLite...`);

      // Store each record
      for (const item of data) {
        await databaseManager.insert(dataType, item);
      }

      console.log(`✅ Successfully stored ${data.length} ${dataType} records in SQLite`);
      return { success: true, count: data.length };
    } catch (error) {
      console.error(`❌ Error storing ${dataType} data:`, error);
      throw error;
    }
  }

  // Get data from SQLite
  async getData(dataType, filters = {}) {
    try {
      if (!this.isInitialized) {
        throw new Error('SQLite storage not initialized');
      }

      console.log(`📖 Getting ${dataType} data from SQLite...`);

      const data = await databaseManager.select(dataType, filters);
      
      console.log(`✅ Retrieved ${data.length} ${dataType} records from SQLite`);
      return data;
    } catch (error) {
      console.error(`❌ Error getting ${dataType} data:`, error);
      throw error;
    }
  }

  // Update data in SQLite
  async updateData(dataType, id, data) {
    try {
      if (!this.isInitialized) {
        throw new Error('SQLite storage not initialized');
      }

      console.log(`✏️ Updating ${dataType} record ${id} in SQLite...`);

      await databaseManager.update(dataType, id, data);
      
      console.log(`✅ Updated ${dataType} record ${id} in SQLite`);
      return { success: true };
    } catch (error) {
      console.error(`❌ Error updating ${dataType} data:`, error);
      throw error;
    }
  }

  // Delete data from SQLite
  async deleteData(dataType, id) {
    try {
      if (!this.isInitialized) {
        throw new Error('SQLite storage not initialized');
      }

      console.log(`🗑️ Deleting ${dataType} record ${id} from SQLite...`);

      await databaseManager.delete(dataType, id);
      
      console.log(`✅ Deleted ${dataType} record ${id} from SQLite`);
      return { success: true };
    } catch (error) {
      console.error(`❌ Error deleting ${dataType} data:`, error);
      throw error;
    }
  }

  // =============================================
  // SYNC OPERATIONS
  // =============================================

  // Sync with Firebase
  async syncWithFirebase() {
    try {
      if (!this.isInitialized) {
        throw new Error('SQLite storage not initialized');
      }

      console.log('🔄 Syncing with Firebase...');

      const result = await databaseManager.sync();
      
      console.log('✅ Sync with Firebase completed');
      return result;
    } catch (error) {
      console.error('❌ Sync with Firebase failed:', error);
      throw error;
    }
  }

  // Get sync status
  async getSyncStatus() {
    try {
      if (!this.isInitialized) {
        return { syncInProgress: false, lastSyncTime: null, pendingRecords: 0 };
      }

      return await databaseManager.getSyncStatus();
    } catch (error) {
      console.error('❌ Get sync status failed:', error);
      throw error;
    }
  }

  // =============================================
  // BILL NUMBER GENERATION
  // =============================================

  // Generate bill number
  async generateBillNumber(billType) {
    try {
      if (!this.isInitialized) {
        throw new Error('SQLite storage not initialized');
      }

      return await databaseManager.generateBillNumber(billType);
    } catch (error) {
      console.error('❌ Generate bill number failed:', error);
      throw error;
    }
  }

  // =============================================
  // DEVICE MANAGEMENT
  // =============================================

  // Register new device
  async registerDevice(deviceType, deviceName, counterId = '') {
    try {
      if (!this.isInitialized) {
        throw new Error('SQLite storage not initialized');
      }

      return await databaseManager.registerDevice(deviceType, deviceName, counterId);
    } catch (error) {
      console.error('❌ Register device failed:', error);
      throw error;
    }
  }

  // Get company devices
  async getCompanyDevices() {
    try {
      if (!this.isInitialized) {
        throw new Error('SQLite storage not initialized');
      }

      return await databaseManager.getCompanyDevices();
    } catch (error) {
      console.error('❌ Get company devices failed:', error);
      throw error;
    }
  }

  // =============================================
  // BACKUP AND RESTORE
  // =============================================

  // Create backup
  async createBackup() {
    try {
      if (!this.isInitialized) {
        throw new Error('SQLite storage not initialized');
      }

      console.log('💾 Creating backup...');

      const result = await databaseManager.createBackup();
      
      console.log('✅ Backup created successfully');
      return result;
    } catch (error) {
      console.error('❌ Create backup failed:', error);
      throw error;
    }
  }

  // Restore from backup
  async restoreFromBackup(backupPath) {
    try {
      if (!this.isInitialized) {
        throw new Error('SQLite storage not initialized');
      }

      console.log('🔄 Restoring from backup...');

      const result = await databaseManager.restoreFromBackup(backupPath);
      
      console.log('✅ Restore from backup completed');
      return result;
    } catch (error) {
      console.error('❌ Restore from backup failed:', error);
      throw error;
    }
  }

  // =============================================
  // DATA CLEARING
  // =============================================

  // Clear all data for specific type
  async clearAllDataForType(dataType) {
    try {
      if (!this.isInitialized) {
        throw new Error('SQLite storage not initialized');
      }

      console.log(`🧹 Clearing all ${dataType} data...`);

      // Get all records for this type
      const records = await databaseManager.select(dataType);
      
      // Delete each record
      for (const record of records) {
        await databaseManager.delete(dataType, record.id);
      }

      console.log(`✅ Cleared all ${dataType} data`);
      return { success: true, count: records.length };
    } catch (error) {
      console.error(`❌ Clear ${dataType} data failed:`, error);
      throw error;
    }
  }

  // Clear all data
  async clearAllData() {
    try {
      if (!this.isInitialized) {
        throw new Error('SQLite storage not initialized');
      }

      console.log('🧹 Clearing all data...');

      const dataTypes = [
        'parties', 'items', 'employees', 'sales_bills', 'challan', 
        'quotation', 'purchase_bills', 'purchase_orders', 'payments', 'expenses'
      ];

      let totalCleared = 0;
      for (const dataType of dataTypes) {
        const result = await this.clearAllDataForType(dataType);
        totalCleared += result.count;
      }

      console.log(`✅ Cleared all data: ${totalCleared} records`);
      return { success: true, count: totalCleared };
    } catch (error) {
      console.error('❌ Clear all data failed:', error);
      throw error;
    }
  }

  // Reset entire database
  async resetDatabase() {
    try {
      if (!this.isInitialized) {
        throw new Error('SQLite storage not initialized');
      }

      console.log('🔄 Resetting entire database...');

      // Close current database
      await databaseManager.close();

      // Reinitialize with same parameters
      await databaseManager.init(this.userId, this.companyId, this.deviceId);

      console.log('✅ Database reset completed');
      return { success: true };
    } catch (error) {
      console.error('❌ Reset database failed:', error);
      throw error;
    }
  }

  // =============================================
  // STATISTICS AND MONITORING
  // =============================================

  // Get storage statistics
  async getStorageStats() {
    try {
      if (!this.isInitialized) {
        return { error: 'SQLite storage not initialized' };
      }

      return await databaseManager.getDatabaseStats();
    } catch (error) {
      console.error('❌ Get storage stats failed:', error);
      throw error;
    }
  }

  // Get device statistics
  async getDeviceStats() {
    try {
      if (!this.isInitialized) {
        return { error: 'SQLite storage not initialized' };
      }

      return await databaseManager.getDeviceStats();
    } catch (error) {
      console.error('❌ Get device stats failed:', error);
      throw error;
    }
  }

  // =============================================
  // UTILITY METHODS
  // =============================================

  // Check if initialized
  isReady() {
    return this.isInitialized;
  }

  // Get current context
  getContext() {
    return {
      userId: this.userId,
      companyId: this.companyId,
      deviceId: this.deviceId,
      isInitialized: this.isInitialized
    };
  }

  // Close storage
  async close() {
    try {
      if (this.isInitialized) {
        await databaseManager.close();
        this.isInitialized = false;
        console.log('✅ SQLite offline storage closed');
      }
    } catch (error) {
      console.error('❌ Close storage failed:', error);
      throw error;
    }
  }
}

// Create singleton instance
const sqliteOfflineStorage = new SQLiteOfflineStorage();

export default sqliteOfflineStorage;
